import React, { useEffect, useState } from "react";

import { Link, useLoaderData, useSearchParams } from "react-router-dom";
import LoaderTable from "../../../../Components/pageLoader/LoaderTable";
import LoaderSpinner from "../../../../Components/pageLoader/LoaderSpinner";
import jsPDF from "jspdf";
import Swal from "sweetalert2";

const FinanceMainPage = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // State to track active tab
  const { products, totalPages, totalPayAmount, totalTax } = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdown, setIsDropdown] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [positionFilter, setPositionFilter] = useState(""); // State for tab filter
  const [phoneNumberFilter, setPhoneNumberFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchParams]);

  const currentPage = parseInt(searchParams.get("page")) || 1;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setSearchParams((prevParams) => {
        prevParams.set("page", nextPage);
        return prevParams;
      });
      setLoading(true);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setSearchParams((prevParams) => {
        prevParams.set("page", prevPage);
        return prevParams;
      });
      setLoading(true);
    }
  };

  const handleApplyFilters = () => {
    setSearchParams((prevParams) => {
      if (dateFilter) prevParams.set("dateFilter", dateFilter);
      if (fromDate) prevParams.set("fromDate", fromDate);
      if (toDate) prevParams.set("toDate", toDate);
      if (phoneNumberFilter) prevParams.set("phoneNumber", phoneNumberFilter);
      if (nameFilter) prevParams.set("name", nameFilter);

      prevParams.set("page", 1);
      return prevParams;
    });
    setLoading(true);
  };

  const handleCancelFilters = () => {
    setDateFilter("");
    setFromDate("");
    setToDate("");
    setPhoneNumberFilter("");
    setNameFilter(""); // ✅ added

    setSearchParams({ page: 1 });
  };

  const handleTabChange = (position) => {
    setPositionFilter(position);
    setSearchParams((prevParams) => {
      prevParams.set("position", position);
      prevParams.set("page", 1);
      return prevParams;
    });
    setLoading(true);
  };

  const toggleFilter = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };
  const formatPosition = (position) => {
    // Add spaces before capital letters and capitalize the first letter
    const withSpaces = position.replace(/([a-z])([A-Z])/g, "$1 $2");
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const finalPayment = totalPayAmount?.toFixed(2);
  const finalTax = totalTax?.toFixed(2);

  const downloadPDF = async (allData = false) => {
    try {
      setLoading(true);

      // Get current filter parameters
      const url = new URL(window.location.href);
      const page = url.searchParams.get("page") || 1;
      const dateFilter = url.searchParams.get("dateFilter") || "";
      const fromDate = url.searchParams.get("fromDate") || "";
      const toDate = url.searchParams.get("toDate") || "";
      const phoneNumber = url.searchParams.get("phoneNumber") || "";
      const name = url.searchParams.get("name") || "";

      // Create PDF in A4 size (210mm x 297mm)
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Set margins (left, top, right, bottom)
      const margin = { left: 15, top: 20, right: 15, bottom: 20 };
      let currentY = margin.top;

      // Title
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Payment Transactions Report", margin.left, currentY);
      currentY += 10;

      // Report metadata
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Generated on: ${new Date().toLocaleString()}`,
        margin.left,
        currentY,
      );
      currentY += 5;

      // Filters info
      let filtersText = "Filters: ";
      if (dateFilter) filtersText += `Date Filter: ${dateFilter} `;
      if (fromDate)
        filtersText += `From: ${new Date(fromDate).toLocaleDateString()} `;
      if (toDate)
        filtersText += `To: ${new Date(toDate).toLocaleDateString()} `;
      if (phoneNumber) filtersText += `Phone: ${phoneNumber}`;
      if (name) filtersText += `name: ${name}`;

      doc.text(filtersText, margin.left, currentY);
      currentY += 8;

      // Initialize variables for all data
      let allProducts = [];
      let totalPayAmount = 0;
      let totalTax = 0;
      let currentPage = 1;
      let hasMoreData = true;

      // Fetch all pages sequentially if allData is true, otherwise just fetch current page
      if (allData) {
        while (hasMoreData) {
          const endpoint = `https://server-gs.vercel.app/stripe/payment-list?page=${currentPage}&limit=20&dateFilter=${dateFilter}&fromDate=${fromDate}&toDate=${toDate}&phoneNumber=${phoneNumber}&name=${name}`;
          const response = await fetch(endpoint);
          const data = await response.json();

          if (data.products && data.products.length > 0) {
            allProducts = [...allProducts, ...data.products];
            totalPayAmount += parseFloat(data.totalPayAmount || 0);
            totalTax += parseFloat(data.totalTax || 0);
            currentPage++;
          } else {
            hasMoreData = false;
          }
        }
      } else {
        // Just fetch current page
        const endpoint = `https://server-gs.vercel.app/stripe/payment-list?page=${page}&limit=50&dateFilter=${dateFilter}&fromDate=${fromDate}&toDate=${toDate}&phoneNumber=${phoneNumber}&name=${name}`;
        const response = await fetch(endpoint);
        const data = await response.json();

        allProducts = data.products || [];
        totalPayAmount = parseFloat(data.totalPayAmount || 0);
        totalTax = parseFloat(data.totalTax || 0);
      }

      // Format the data for the table
      const tableData = allProducts.map((item) => [
        item.name || "N/A",
        item.email || "N/A",
        item.phoneNumber ? formatPhoneNumber(item.phoneNumber) : "N/A",
        item.address || "N/A",
        item.paymentMethod ? formatPaymentMethod(item.paymentMethod) : "N/A",
        item.payAmount ? `$${parseFloat(item.payAmount)}` : "$0.00",
        item.tax ? `$${parseFloat(item.tax)}` : "$0.00",
        item.PaymentDate ? formatDate(item.PaymentDate) : "N/A",
        item.invoicePaymentId || "N/A",
      ]);

      // Calculate available width (A4 width - margins)
      const availableWidth = 210 - margin.left - margin.right;

      // Add table with proper column widths
      doc.autoTable({
        head: [
          [
            "Name",
            "Email",
            "Phone",
            "Address",
            "Method",
            "Amount",
            "Tax",
            "Date",
            "Invoice ID",
          ],
        ],
        body: tableData,
        startY: currentY,
        margin: margin,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          halign: "left",
        },
        columnStyles: {
          0: { cellWidth: availableWidth * 0.15 }, // Name
          1: { cellWidth: availableWidth * 0.2 }, // Email
          2: { cellWidth: availableWidth * 0.1 }, // Phone
          3: { cellWidth: availableWidth * 0.15 }, // Address
          4: { cellWidth: availableWidth * 0.08 }, // Method
          5: { cellWidth: availableWidth * 0.08 }, // Amount
          6: { cellWidth: availableWidth * 0.08 }, // Tax
          7: { cellWidth: availableWidth * 0.08 }, // Date
          8: { cellWidth: availableWidth * 0.08 }, // Invoice ID
        },
        didDrawPage: function (data) {
          // Footer on each page
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.text(
            `Page ${doc.internal.getNumberOfPages()}`,
            availableWidth / 2,
            297 - margin.bottom + 5,
            { align: "center" },
          );
        },
      });

      // Get final Y position after table
      currentY = doc.lastAutoTable.finalY + 5;

      // Summary section only if there's space
      if (currentY < 297 - margin.bottom - 15) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`Summary`, margin.left, currentY);
        currentY += 5;

        doc.setFont("helvetica", "normal");
        doc.text(`Total Payments: $${finalPayment}`, margin.left, currentY);
        currentY += 5;
        doc.text(`Total Tax: $${finalTax}`, margin.left, currentY);
        currentY += 5;
        doc.text(`Total Records: ${allProducts.length}`, margin.left, currentY);
      } else {
        // Add new page for summary if no space
        doc.addPage();
        currentY = margin.top;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(`Summary`, margin.left, currentY);
        currentY += 5;

        doc.setFont("helvetica", "normal");
        doc.text(`Total Payments: $${finalPayment}`, margin.left, currentY);
        currentY += 5;
        doc.text(`Total Tax: $${finalTax}`, margin.left, currentY);
        currentY += 5;
        doc.text(`Total Records: ${allProducts.length}`, margin.left, currentY);
      }

      // Save the PDF
      const fileName = allData
        ? `All_Payments_${new Date().toISOString().slice(0, 10)}.pdf`
        : `Payments_Page_${page}_${new Date().toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      // Optional: Show error to user
    } finally {
      setLoading(false);
    }
  };

  // Helper functions (keep these the same as before)
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPhoneNumber = (phone) => {
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
  };

  const formatPaymentMethod = (method) => {
    return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
  };

  const handleDelete = async (orderId) => {
    // Show a confirmation dialog using SweetAlert2
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this transaction? This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
    });

    // If the user cancels, exit the function
    if (!result.isConfirmed) {
      return;
    }

    try {
      // Send a DELETE request to the backend API with the order ID
      const response = await fetch(
        "https://server-gs.vercel.app/delete-stripe-pay",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ orderId }), // Send the orderId in the request body
        },
      );

      // Check if the deletion was successful
      if (!response.ok) {
        const data = await response.json();
        Swal.fire(
          "Error",
          data.error || "Failed to delete transaction",
          "error",
        );
        return;
      }

      // If successful, notify the user with auto-closing Swal
      Swal.fire({
        title: "Deleted!",
        text: "Transaction deleted successfully.",
        icon: "success",
        timer: 300, // Auto-close after 2 seconds
        timerProgressBar: true,
        showConfirmButton: false, // Hide "OK" button
      });
      window.location.reload(); // ✅ reload page after Swal closes

      // Optionally update UI without reloading
      // Example: setOrders(prevOrders => prevOrders.filter(order => order._id !== orderId));
    } catch (error) {
      console.error("Error deleting order:", error);
      Swal.fire("Error", "An error occurred while deleting the order", "error");
    }
  };

  return (
   <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans">
      {loading ? (
        <LoaderSpinner />
      ) : (
        <div className="flex flex-col">
          
          {/* TOP NAVIGATION BAR */}
          <header className="sticky top-0 z-10 w-full bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4">
            <div className="max-w-[1600px] mx-auto flex justify-between items-center">
              <div className="flex items-center gap-4">
                <button
                  onClick={toggleFilter}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200 group"
                  title="Toggle Filters"
                >
                  {isFilterOpen ? (
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                      Hide Filters
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                      Filters
                    </span>
                  )}
                </button>
                <h1 className="text-xl font-bold tracking-tight text-slate-800">Transactions</h1>
              </div>

              <button
                onClick={() => downloadPDF(true)}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-5 rounded-lg shadow-sm transition-all active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                Export Report
              </button>
            </div>
          </header>

          <div className="flex flex-row w-full h-[calc(100vh-73px)] overflow-hidden">
            
            {/* SIDEBAR FILTERS */}
            <aside
              className={`bg-white border-r border-slate-200 transition-all duration-300 ease-in-out overflow-y-auto ${
                isFilterOpen ? "w-[28%] opacity-100" : "w-0 opacity-0"
              }`}
            >
              <div className="p-6 space-y-8">
                <section>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Customer Search</h3>
                  <div className="space-y-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-slate-700">Client Name</label>
                      <input
                        type="text"
                        placeholder="e.g. John Doe"
                        className="w-full rounded-md border-slate-200 text-sm focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-slate-700">Phone Number</label>
                      <input
                        type="text"
                        placeholder="+1 (555) 000-0000"
                        className="w-full rounded-md border-slate-200 text-sm focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                        value={phoneNumberFilter}
                        onChange={(e) => setPhoneNumberFilter(e.target.value)}
                      />
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Date Range</h3>
                  <div className="space-y-4">
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="w-full rounded-md border-slate-200 text-sm focus:ring-indigo-500"
                    >
                                <option value="">Select Dates</option>
          <option value="previous90days">Previous 90 Days</option>
          <option value="previous60days">Previous 60 Days</option>
          <option value="previous30days">Previous 30 Days</option>
          <option value="previous14days">Previous 14 Days</option>
          <option value="previous7days">Previous 7 Days</option>
          <option value="yesterday">Yesterday</option>
          <option value="today">Today</option>
          <option value="tomorrow">Tomorrow</option>
          <option value="next7days">Next 7 Days</option>
          <option value="next14days">Next 14 Days</option>
          <option value="next30days">Next 30 Days</option>
          <option value="next60days">Next 60 Days</option>
          <option value="next90days">Next 90 Days</option>
                    </select>

                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="text-xs font-medium text-slate-500">From</label>
                        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full mt-1 rounded-md border-slate-200 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">To</label>
                        <input type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} className="w-full mt-1 rounded-md border-slate-200 text-sm" />
                      </div>
                    </div>
                  </div>
                </section>

                <div className="flex flex-col gap-3 pt-4">
                  <button onClick={handleApplyFilters} className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 shadow-md transition-all">
                    Apply Filters
                  </button>
                  <button onClick={handleCancelFilters} className="w-full bg-slate-100 text-slate-600 py-2.5 rounded-lg font-medium hover:bg-slate-200 transition-all">
                    Reset
                  </button>
                </div>
              </div>
            </aside>

            {/* MAIN DATA SECTION */}
            <main className="flex-1 overflow-y-auto p-6 lg:p-8">
              <div className="max-w-[1400px] mx-auto space-y-6">
                
                {/* SUMMARY CARDS */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Total Tax Collected</p>
                      <h2 className="text-2xl font-bold text-slate-800">${finalTax}</h2>
                    </div>
                    <div className="bg-amber-50 p-3 rounded-lg text-amber-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Net Revenue</p>
                      <h2 className="text-2xl font-bold text-indigo-600">${finalPayment}</h2>
                    </div>
                    <div className="bg-indigo-50 p-3 rounded-lg text-indigo-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                  </div>
                </div>

                {/* TABLE CARD */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-200">
                          <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Client</th>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Phone</th>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Method</th>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase text-center">Amount</th>
                          <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {products.map((product) => (
                          <tr key={product._id} className="hover:bg-slate-50/80 transition-colors group">
                            <td className="px-6 py-4">
                              <div className="flex flex-col">
                                <span className="font-semibold text-slate-700">{product.name}</span>
                                <span className="text-xs text-slate-400">{product.email}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600">{product.phoneNumber}</td>
                            <td className="px-6 py-4 text-sm text-slate-600">
                                {new Date(product.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                {product.paymentMethod || 'Stripe'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center font-bold text-slate-700">
                              ${product.payAmount}
                            </td>
                            <td className="px-6 py-4 text-center">
                              <button 
                                onClick={() => handleDelete(product._id)}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* PAGINATION */}
                  <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                    <p className="text-sm text-slate-500">
                      Showing <span className="font-medium">Page {currentPage}</span> of <span className="font-medium">{totalPages}</span>
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors shadow-sm"
                      >
                        Previous
                      </button>
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors shadow-sm"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceMainPage;
